import { DOCUMENT } from '@angular/common';
import { Component, OnInit, OnDestroy, inject, HostListener, ViewChild, ElementRef, Renderer2 } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { finalize, takeUntil } from 'rxjs';

import { StorageService } from 'ngx-webstorage-service';

import { ApiService, AppStorageService, LoadingSpinnerService, NavigationService, PhxLocalizationService } from '@common';
import { BaseComponentOnDestroy } from '@common/epics/base-component-on-destroy';
import { ToastService } from '@common/services/toast.service';

import { AppLoaderData } from './models/app-loader-data.model';

@Component({
  selector: 'app-loader',
  templateUrl: './app-loader.component.html',
  styleUrls: ['./app-loader.component.less'],
  standalone: true
})
export class AppLoaderComponent extends BaseComponentOnDestroy implements OnInit, OnDestroy {
  iframeSource?: SafeResourceUrl;
  @ViewChild('appIframe') appIframe?: ElementRef<HTMLIFrameElement>;

  private readonly navigationService = inject(NavigationService);
  private readonly storageService: StorageService = inject(AppStorageService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly toastService = inject(ToastService);
  private readonly loadingSpinnerService = inject(LoadingSpinnerService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly apiService = inject(ApiService);
  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  private isRefreshingToken = false;
  private readonly bodyCssClass = 'app-loader-active';

  ngOnDestroy(): void {
    this.navigationService.updateRemovePaddingMarginState(false);
    this.renderer.removeClass(this.document.body, this.bodyCssClass);
    super.ngOnDestroy();
  }

  ngOnInit(): void {
    this.navigationService.disableBreadCrumbsForComponent(this.isDestroyed$);
    this.navigationService.updateRemovePaddingMarginState(true);
    this.renderer.addClass(this.document.body, this.bodyCssClass);
    this.loadApplication();
  }

  handleIframeLoad(hasLoaded: boolean) {
    if (!hasLoaded) {
      this.toastService.logError('Failed to load application');
    }
    this.loadingSpinnerService.hide();
  }

  private loadApplication(): void {
    const { uri, queryParams } = this.activatedRoute.snapshot.data.appLoaderData as AppLoaderData;

    if (!uri) {
      const code = this.activatedRoute.snapshot.params.code;
      this.toastService.logError(`Could not find application with code: ${code}!`);
      return;
    }

    this.loadingSpinnerService.show();

    const queryParamsString = Object.entries(queryParams)
      .filter(([, value]) => value)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');

    let fullUrl = `${uri}${uri.includes('?') ? '&' : '?'}culture=${PhxLocalizationService.locale}`;

    if (queryParamsString) {
      fullUrl += `&${queryParamsString}`;
    }

    this.iframeSource = this.sanitizer.bypassSecurityTrustResourceUrl(fullUrl);
  }

  @HostListener('window:message', ['$event'])
  private handleReceivedMessage(event: MessageEvent): void {
    if (event.data && event.data.action === 'REQUEST_TOKEN') {
      if (!this.isRefreshingToken) {
        this.isRefreshingToken = true;
        this.apiService
          .refreshToken()
          .pipe(
            takeUntil(this.isDestroyed$),
            finalize(() => {
              this.isRefreshingToken = false;
            })
          )
          .subscribe(() => {
            const token = this.storageService.get('BearerToken');
            this.appIframe?.nativeElement.contentWindow?.postMessage({ action: 'SET_TOKEN', token }, '*');
          });
      }
    }
  }
}
